import { useState, useEffect } from 'react';
import { useLocation } from '@reach/router';
import { getOnPageFormDefault, isSelfSchedulePath, ON_PAGE_FORM_ENABLED } from '../utils/selfScheduleConfig';

// Extend Window interface to allow Optimizely to set a synchronous override flag
// before React mounts, so the hook can pick it up on mount.
declare global {
    interface Window {
        __selfScheduleTest?: boolean | string | number;
    }
}

const EVENT_NAME = 'self_schedule_test';

export type SelfScheduleVariant = '0' | '1' | '2';

// '0': explicit control (or a bare 0) — the standard form, even over a page's
//      ssa_onpage default. Distinct from null, "nothing sent" (WEBDEV-4374).
// '1': redirect consult-form CTAs to /schedule and hide the legacy on-page
//      form (WEBDEV-3681) — no visible on-page form.
// '2': same as '1', PLUS show the new on-page self-schedule form introduced
//      in WEBDEV-4039 (WEBDEV-4173 split this out of the single '1' flag).
// Bare true/'true'/'on' are the pre-WEBDEV-4173 values for what was then the
// only variant — kept equivalent to '1' for any campaign still sending them.
// Each variant is also accepted as a bare number, so a campaign sending
// `detail: 2` is not silently handed the page default instead.
function parseVariant(value: unknown): SelfScheduleVariant | null {
    if (value === '2' || value === 2) return '2';
    if (value === true || value === '1' || value === 1 || value === 'true' || value === 'on') return '1';
    if (value === '0' || value === 0) return '0';
    return null;
}

/**
 * Self-schedule experiment flag (WEBDEV-3681; first rolled out on /specials).
 *
 * Allows Optimizely (or any other caller) to toggle the "self schedule test"
 * variant by dispatching a CustomEvent or setting window.__selfScheduleTest
 * before React mounts.
 *
 * Returns `{ variant }`, the raw, page-agnostic value — most call sites want
 * useSelfScheduleFlags(), which also checks the current path is one we run
 * the experiment on.
 *
 * The Optimizely snippet is expected to reset the flag and dispatch a falsy
 * detail on SPA navigation away, and re-toggle on return, so the variant follows
 * the page rather than the session.
 *
 * Flicker: there is intentionally no "revealed" gate here (unlike the removed
 * usePromoModuleOrder hook, which protected an ABOVE-fold element and so hid it
 * until the variant resolved). Here the only variant-dependent element is the
 * ConsultationForm, which Layout renders BELOW the fold — gating it would delay
 * the primary lead form on every control load for no visible benefit.
 * Preventing the brief form→stub swap on a cold load is therefore left to
 * Optimizely's anti-flicker snippet (set the flag before paint); worst case is
 * a sub-second below-fold flash, not an above-fold layout shift.
 *
 * Example usage from Optimizely campaign JS:
 *   window.__selfScheduleTest = '1'; // or '2' — see SelfScheduleVariant above
 *   window.dispatchEvent(new CustomEvent('self_schedule_test', { detail: '1' }));
 *   // control is an explicit '0', which overrides a page's flag default (WEBDEV-4374)
 *   // on navigate away — null, never '0', so the next page gets its own default:
 *   window.__selfScheduleTest = undefined;
 *   window.dispatchEvent(new CustomEvent('self_schedule_test', { detail: null }));
 */
export function useSelfScheduleVariant(): { variant: SelfScheduleVariant | null } {
    // Starts at null on the client too, and the window flag is read after mount:
    // the first client render must match the static HTML, which Gatsby built with
    // no variant (null, so the page default). Reading the flag in a useState
    // initializer made any visit with the flag preset fail hydration (React
    // #418) and re-render the whole page on the client — every control visit,
    // once a page defaults to ssa_onpage (WEBDEV-4374).
    const [variant, setVariant] = useState<SelfScheduleVariant | null>(null);

    useEffect(() => {
        if (typeof window === 'undefined') return;

        setVariant(parseVariant(window.__selfScheduleTest));

        const handler = (event: Event) => {
            setVariant(parseVariant((event as CustomEvent<unknown>).detail));
        };

        window.addEventListener(EVENT_NAME, handler);

        return () => window.removeEventListener(EVENT_NAME, handler);
    }, []);

    return { variant };
}

/**
 * Resolved self-schedule flags for a page the experiment runs on (see
 * SELF_SCHEDULE_PATHS) — the single gate every call site should use, so the
 * variant subscription in useSelfScheduleVariant() is only mounted once per
 * component even where both flags are needed.
 *
 * - `active`: variant '1' or '2' in effect. Gates the behavior both share —
 *   suppressing the legacy consult form and driving the CTA interceptor.
 *   True even if `onPageFormActive` is false (variant '1', or variant '2'
 *   with the on-page form off — see below), so callers still swap the legacy
 *   form for the hidden stub in that case.
 * - `onPageFormActive`: variant '2' specifically, AND the on-page form's own
 *   corporate-side kill-switch (ON_PAGE_FORM_ENABLED, WEBDEV-4043) is on.
 *   Gates showing the on-page self-schedule form (WEBDEV-4039); variant '1'
 *   never sets this, only redirects CTAs and hides the legacy form
 *   (WEBDEV-4173).
 *
 * With no variant sent, a page whose flag defaults to `ssa_onpage`
 * (getOnPageFormDefault, WEBDEV-4374) resolves as variant '2' — both flags, so
 * the CTA interceptor follows the default too. The default is derived from the
 * pathname on every render, so it applies to the SSR'd HTML and never carries
 * across a navigation. With the kill-switch off the default is ignored, since
 * resolving it as '2' would hide the consult form and redirect every CTA; an
 * explicit '2' keeps today's stub-and-redirect behavior in that case.
 *
 * Re-evaluates on SPA navigation via useLocation.
 */
export function useSelfScheduleFlags(): { active: boolean; onPageFormActive: boolean } {
    const { variant } = useSelfScheduleVariant();
    const { pathname } = useLocation();
    const onSelfSchedulePath = isSelfSchedulePath(pathname);

    // An explicit variant wins; only when Optimizely has sent nothing does the page default apply.
    const defaultIsOnPage = ON_PAGE_FORM_ENABLED && getOnPageFormDefault(pathname) === 'ssa_onpage';
    const defaultVariant = defaultIsOnPage ? '2' : '0';
    const effectiveVariant = variant ?? defaultVariant;

    return {
        active: effectiveVariant !== '0' && onSelfSchedulePath,
        onPageFormActive: effectiveVariant === '2' && onSelfSchedulePath && ON_PAGE_FORM_ENABLED,
    };
}
